import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { useAuth } from "./AuthContext";

const CartContext = createContext(null);

// Il carrello è per-utente e persistito in localStorage. Solo aziende e privati
// (studenti senza organizzazione) possono acquistare: per gli altri è inattivo.
export function CartProvider({ children }) {
  const { user } = useAuth();
  const userId = user?.id || null;
  const canBuy =
    user?.role === "azienda" ||
    (user?.role === "studente" && !user?.organization_id);
  const isIndividual = user?.role === "studente";

  const [items, setItems] = useState([]);

  const storageKey = userId ? `burocratica_cart_${userId}` : null;

  useEffect(() => {
    if (!storageKey) { setItems([]); return; }
    try {
      const raw = localStorage.getItem(storageKey);
      setItems(raw ? JSON.parse(raw) : []);
    } catch {
      setItems([]);
    }
  }, [storageKey]);

  useEffect(() => {
    if (storageKey) localStorage.setItem(storageKey, JSON.stringify(items));
  }, [items, storageKey]);

  const addItem = useCallback((course, seats = 1) => {
    const qty = isIndividual ? 1 : Math.max(1, seats);
    setItems((prev) => {
      const existing = prev.find((i) => i.course_id === course.id);
      if (existing) {
        // Il privato resta a 1 posto per corso; l'azienda somma i posti.
        if (isIndividual) return prev;
        return prev.map((i) =>
          i.course_id === course.id ? { ...i, seats: i.seats + qty } : i
        );
      }
      return [
        ...prev,
        {
          course_id: course.id,
          title: course.title,
          price: course.price,
          area: course.area,
          level: course.level,
          seats: qty,
        },
      ];
    });
  }, [isIndividual]);

  const setSeats = useCallback((courseId, seats) => {
    setItems((prev) =>
      prev.map((i) => (i.course_id === courseId ? { ...i, seats: Math.max(1, seats) } : i))
    );
  }, []);

  const removeItem = useCallback((courseId) => {
    setItems((prev) => prev.filter((i) => i.course_id !== courseId));
  }, []);

  const clear = useCallback(() => setItems([]), []);

  return (
    <CartContext.Provider
      value={{ items, addItem, setSeats, removeItem, clear, count: items.length, canBuy, isIndividual }}
    >
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
}
